import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon, Link as LinkIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';

export default function CoversPairsTab() {
  const { success: showSuccess, error: showError } = useNotify();
  const [poufParent, setPoufParent] = useState(null);
  const [coverParent, setCoverParent] = useState(null);
  const [poufFamily, setPoufFamily] = useState({ parent: null, variants: [] });
  const [coverFamily, setCoverFamily] = useState({ parent: null, variants: [] });
  const [poufOptions, setPoufOptions] = useState([]);
  const [coverOptions, setCoverOptions] = useState([]);
  const [selectedPouf, setSelectedPouf] = useState(null);
  const [selectedCover, setSelectedCover] = useState(null);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const searchParents = async (q, setter, onlyCover = false) => {
    try {
      const { data } = await axios.get('/ms/search-products', { params: { q, limit: 20, onlyCover } });
      setter(data.items || []);
    } catch (_) { setter([]); }
  };

  const loadFamily = async (parentId, setter) => {
    if (!parentId) { setter({ parent: null, variants: [] }); return; }
    try {
      const { data } = await axios.get('/ms/product-family', { params: { parentId } });
      setter({ parent: data.parent, variants: data.variants || [] });
      await refreshList();
    } catch (_) { setter({ parent: null, variants: [] }); }
  };

  const refreshList = async () => {
    try {
      const params = {};
      if (poufParent?.id) params.parentPouf = poufParent.id;
      if (coverParent?.id) params.parentCover = coverParent.id;
      if (searchQuery) params.q = searchQuery;
      const { data } = await axios.get('/ms/bindings/list', { params });
      setList(data.mappings || []);
    } catch (_) { setList([]); }
  };

  const handleBind = async () => {
    if (!selectedPouf || !selectedCover) return;
    try {
      setSaving(true);
      await axios.post('/ms/bindings/set', { puf_id: selectedPouf.id_ms, chehol_id: selectedCover.id_ms });
      showSuccess('Связь создана');
      setSelectedPouf(null);
      setSelectedCover(null);
      await refreshList();
    } catch (e) {
      showError(e?.response?.data?.message || 'Ошибка создания связи');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (puf_id) => {
    try {
      await axios.delete('/ms/bindings/remove', { data: { puf_id } });
      showSuccess('Связь удалена');
      await refreshList();
    } catch (e) {
      showError(e?.response?.data?.message || 'Ошибка удаления связи');
    }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    try {
      const params = { q };
      if (poufParent?.id) params.parentPouf = poufParent.id;
      if (coverParent?.id) params.parentCover = coverParent.id;
      const { data } = await axios.get('/ms/bindings/list', { params });
      setList(data.mappings || []);
    } catch (_) { setList([]); }
  };

  useEffect(() => { refreshList(); }, []);

  const cellStyle = { py: 0.5, px: 1, fontSize: '0.85rem' };
  const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Пара пуф ↔ чехол для производства (подстановка чехла к пуфу в заданиях). В остаток на МП не влияет.
      </Typography>

      <Grid container spacing={2}>
        {/* Левая колонка - Пуфы */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Autocomplete
              options={poufOptions}
              getOptionLabel={(o) => o.name || ''}
              onOpen={() => searchParents('', setPoufOptions)}
              onInputChange={(_, val) => searchParents(val, setPoufOptions)}
              onChange={(_, val) => { setPoufParent(val); loadFamily(val?.id, setPoufFamily); }}
              renderInput={(params) => <TextField {...params} label="Товар (Пуф)" size="small" />}
              size="small"
            />
            <TableContainer sx={{ mt: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerStyle}>Модификация</TableCell>
                    <TableCell sx={headerStyle}>ШК</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {poufFamily.variants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 3 }}>Выберите товар</TableCell>
                    </TableRow>
                  ) : (
                    poufFamily.variants.map(v => {
                      const isBound = list.some(m => m.puf_id === v.id_ms);
                      const isSelected = selectedPouf?.id_ms === v.id_ms;
                      return (
                        <TableRow
                          key={v.id_ms}
                          hover
                          selected={isSelected}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isSelected ? 'primary.light' : isBound ? '#c8e6c9' : undefined,
                          }}
                          onClick={() => setSelectedPouf(v)}
                        >
                          <TableCell sx={cellStyle}>{v.name_ms}</TableCell>
                          <TableCell sx={cellStyle}>{v.barcode_ms}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Правая колонка - Чехлы */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Autocomplete
              options={coverOptions}
              getOptionLabel={(o) => o.name || ''}
              onOpen={() => searchParents('', setCoverOptions, true)}
              onInputChange={(_, val) => searchParents(val, setCoverOptions, true)}
              onChange={(_, val) => { setCoverParent(val); loadFamily(val?.id, setCoverFamily); }}
              renderInput={(params) => <TextField {...params} label="Товар (Чехол)" size="small" />}
              size="small"
            />
            <TableContainer sx={{ mt: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerStyle}>Модификация</TableCell>
                    <TableCell sx={headerStyle}>ШК</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coverFamily.variants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 3 }}>Выберите товар</TableCell>
                    </TableRow>
                  ) : (
                    coverFamily.variants.map(v => {
                      const isBound = list.some(m => m.chehol_id === v.id_ms);
                      const isSelected = selectedCover?.id_ms === v.id_ms;
                      return (
                        <TableRow
                          key={v.id_ms}
                          hover
                          selected={isSelected}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isSelected ? 'primary.light' : isBound ? '#c8e6c9' : undefined,
                          }}
                          onClick={() => setSelectedCover(v)}
                        >
                          <TableCell sx={cellStyle}>{v.name_ms}</TableCell>
                          <TableCell sx={cellStyle}>{v.barcode_ms}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Панель действий */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            disabled={!selectedPouf || !selectedCover || saving}
            onClick={handleBind}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
          >
            Связать выбранные
          </Button>
          <TextField
            size="small"
            placeholder="Поиск в связях"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Button variant="outlined" onClick={refreshList} startIcon={<RefreshIcon />}>
            Обновить
          </Button>
        </Box>
      </Paper>

      {/* Таблица связей */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerStyle}>Чехол</TableCell>
              <TableCell sx={headerStyle}>Пуф</TableCell>
              <TableCell sx={headerStyle} align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>Нет связей</TableCell>
              </TableRow>
            ) : (
              list.map(row => (
                <TableRow key={row.ID} hover>
                  <TableCell sx={cellStyle}>{row.chehol_name} ({row.chehol})</TableCell>
                  <TableCell sx={cellStyle}>{row.puf_name} ({row.puf})</TableCell>
                  <TableCell sx={cellStyle} align="right">
                    <Button
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(row.puf_id)}
                    >
                      Удалить
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
